import React, { useEffect, useState } from 'react'
import { Check, RotateCcw, Save, Loader2, KeyRound, Lock } from 'lucide-react'
import { useStrategies } from '../hooks/useRag'
import {
  useDefaults,
  useSaveDefaults,
  useEngineSettings,
  useSaveTranscribeConfig,
} from '../hooks/useSettings'
import { useQuery } from '@tanstack/react-query'
import { bff } from '../services/bff'
import { useTranslateConfig } from '../hooks/useResearch'
import McpUsagePanel from '../components/McpUsagePanel'
import AccountsPanel from '../components/AccountsPanel'
import { TemplateGallery } from '../components/SlidePreview'

const TRANSCRIBE_PROVIDERS = ['', 'whisper', 'google', 'gemini', 'deepgram', 'assemblyai']
const WHISPER_SIZES = ['', 'tiny', 'base', 'small', 'medium', 'large-v3']
const RAG_KEYS = ['openai', 'google', 'anthropic', 'voyage']
const TRANSCRIBE_KEYS = ['anthropic', 'gemini', 'deepgram', 'assemblyai']
const OVERVIEW_LANGS = [['cs', 'Czech'], ['en', 'English'], ['sk', 'Slovak'], ['de', 'German'], ['pl', 'Polish'], ['ru', 'Russian']]
// model suggestions for the Overviews LLM picker (free text — any model id the provider serves works)
const LLM_MODEL_HINTS = {
  anthropic: ['claude-sonnet-5', 'claude-opus-4-8', 'claude-haiku-4-5-20251001'],
  gemini: ['gemini-2.5-flash', 'gemini-2.5-pro', 'gemini-3.1-pro-preview'],
  openai: ['gpt-4o', 'gpt-4o-mini'],
}

// --- shared field styling (brand idiom: parchment / midnight, gold focus) ---
const inputCls =
  'h-9 w-full rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink ' +
  'focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist'

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-ink-soft dark:text-mist/60 mb-1">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-[11px] text-ink-soft/70 dark:text-mist/40">{hint}</span>}
    </label>
  )
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-parchment-border text-gold focus:ring-gold/50 dark:border-midnight-600"
      />
      <span className="text-sm text-ink dark:text-mist">{label}</span>
    </label>
  )
}

function KeyChip({ name, present }) {
  return (
    <span
      className={
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ' +
        (present
          ? 'bg-spark/10 text-spark-600 border-spark/30 dark:text-spark'
          : 'bg-parchment-100 text-ink-soft border-parchment-border dark:bg-midnight-700 dark:text-mist/50 dark:border-midnight-600')
      }
    >
      <span className={'w-1.5 h-1.5 rounded-full ' + (present ? 'bg-spark' : 'bg-ink-soft/40')} />
      {name}
    </span>
  )
}

function Section({ title, subtitle, children, footer }) {
  return (
    <section className="rounded-xl border border-parchment-border bg-white/70 dark:border-midnight-600 dark:bg-midnight-800/60">
      <div className="px-6 py-4 border-b border-parchment-border dark:border-midnight-600">
        <h2 className="font-display text-lg text-ink dark:text-mist">{title}</h2>
        {subtitle && <p className="mt-0.5 text-sm text-ink-soft dark:text-mist/60">{subtitle}</p>}
      </div>
      <div className="p-6 space-y-5">{children}</div>
      {footer && (
        <div className="px-6 py-4 border-t border-parchment-border dark:border-midnight-600 flex items-center gap-3">
          {footer}
        </div>
      )}
    </section>
  )
}

function SaveButton({ onClick, saving, saved, disabled, label = 'Save' }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || saving}
      className="inline-flex items-center gap-2 px-4 h-9 rounded-lg text-sm font-medium bg-gradient-to-r from-gold to-spark text-midnight-900 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
    >
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
      {saving ? 'Saving…' : saved ? 'Saved' : label}
    </button>
  )
}

// ============================== Defaults ==============================
function DefaultsForm() {
  const { data, isLoading } = useDefaults()
  const { data: strat } = useStrategies()
  const { data: tcfg } = useTranslateConfig()
  const save = useSaveDefaults()
  const [form, setForm] = useState(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (data && !form) setForm(structuredClone(data))
  }, [data, form])

  if (isLoading || !form) {
    return <div className="text-sm text-ink-soft dark:text-mist/50">Loading defaults…</div>
  }

  const set = (section, key, value) => {
    setForm((f) => ({ ...f, [section]: { ...f[section], [key]: value } }))
    setSaved(false)
  }
  const onSave = () => save.mutate(form, { onSuccess: () => setSaved(true) })
  const onReset = () => {
    setForm(structuredClone(data))
    setSaved(false)
  }
  const strategies = strat?.strategies ?? []

  return (
    <Section
      title="Per-call defaults"
      subtitle="Values Veralis applies when a Search, Ask or Import request leaves a field blank. An explicit choice in the page always wins."
      footer={
        <>
          <SaveButton onClick={onSave} saving={save.isPending} saved={saved && !save.isPending} />
          <button
            onClick={onReset}
            className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink dark:text-mist/50 dark:hover:text-mist"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
          {save.isError && <span className="text-sm text-red-600">{save.error?.message}</span>}
        </>
      }
    >
      {/* Storage — where generated artifacts land */}
      <div>
        <h3 className="font-display text-sm uppercase tracking-[0.14em] text-gold-deep dark:text-gold mb-3">
          Storage
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Reports directory" hint="Where generated HTML reports are written. Blank = bff/data/reports. Older reports remain readable from the previous location.">
            <input
              className={inputCls}
              value={form.storage?.reports_dir ?? ''}
              onChange={(e) => set('storage', 'reports_dir', e.target.value)}
              placeholder="C:\\path\\to\\reports (blank = built-in)"
            />
          </Field>
          <Field label="MkDocs export directory" hint="Docs exports land in <dir>/<repo-name>. Blank = bff/data/docs-export.">
            <input
              className={inputCls}
              value={form.storage?.docs_export_dir ?? ''}
              onChange={(e) => set('storage', 'docs_export_dir', e.target.value)}
              placeholder="C:\\path\\to\\docs-export (blank = built-in)"
            />
          </Field>
        </div>
      </div>

      {/* Retrieval & answers */}
      <div>
        <h3 className="font-display text-sm uppercase tracking-[0.14em] text-gold-deep dark:text-gold mb-3">
          Ask &amp; Search
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Answer strategy" hint="Used by the Ask mode in Search.">
            <select className={inputCls} value={form.rag.strategy} onChange={(e) => set('rag', 'strategy', e.target.value)}>
              <option value="">Engine default</option>
              {strategies.map((s) => (
                <option key={s.name} value={s.name}>{s.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Response language" hint="e.g. English, Czech. Blank = match the question.">
            <input
              className={inputCls}
              value={form.rag.response_language}
              onChange={(e) => set('rag', 'response_language', e.target.value)}
              placeholder="auto"
            />
          </Field>
        </div>
        <div className="mt-3">
          <Toggle
            label="Use the answer cache by default"
            checked={form.rag.use_cache}
            onChange={(v) => set('rag', 'use_cache', v)}
          />
        </div>
      </div>

      {/* Transcription */}
      <div className="pt-2 border-t border-parchment-border dark:border-midnight-600">
        <h3 className="font-display text-sm uppercase tracking-[0.14em] text-gold-deep dark:text-gold mb-3 mt-3">
          Transcription (Import)
        </h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Provider">
            <select className={inputCls} value={form.transcribe.provider} onChange={(e) => set('transcribe', 'provider', e.target.value)}>
              {TRANSCRIBE_PROVIDERS.map((p) => (
                <option key={p} value={p}>{p || 'Engine default'}</option>
              ))}
            </select>
          </Field>
          <Field label="Whisper model size" hint="Only applies to the whisper provider.">
            <select className={inputCls} value={form.transcribe.model_size} onChange={(e) => set('transcribe', 'model_size', e.target.value)}>
              {WHISPER_SIZES.map((m) => (
                <option key={m} value={m}>{m || 'Engine default'}</option>
              ))}
            </select>
          </Field>
          <Field label="Language" hint="Blank = auto-detect.">
            <input className={inputCls} value={form.transcribe.language} onChange={(e) => set('transcribe', 'language', e.target.value)} placeholder="auto" />
          </Field>
        </div>
        <div className="mt-3 flex flex-wrap gap-6">
          <Toggle label="Clean audio first" checked={form.transcribe.audio_clean} onChange={(v) => set('transcribe', 'audio_clean', v)} />
          <Toggle label="LLM transcript cleanup" checked={form.transcribe.cleanup} onChange={(v) => set('transcribe', 'cleanup', v)} />
        </div>
      </div>

      {/* Translation */}
      <div className="pt-2 border-t border-parchment-border dark:border-midnight-600">
        <h3 className="font-display text-sm uppercase tracking-[0.14em] text-gold-deep dark:text-gold mb-3 mt-3">
          Translation
        </h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Provider" hint="light = LLM, no Docker; t8r = backend (LAN/VPN).">
            <select className={inputCls} value={form.translate.provider || 'light'} onChange={(e) => set('translate', 'provider', e.target.value)}>
              <option value="light">Light (LLM)</option>
              <option value="t8r">T8R</option>
            </select>
          </Field>
          {(form.translate.provider || 'light') === 'light' && (
            <>
              <Field label="Light LLM" hint="Uses local-rag's key. Blank = engine default.">
                <select className={inputCls} value={form.translate.llm_provider || ''} onChange={(e) => set('translate', 'llm_provider', e.target.value)}>
                  <option value="">Default{tcfg?.provider ? ` (${tcfg.provider})` : ''}</option>
                  {(tcfg?.available_providers ?? []).map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Light model" hint="Cheap/fast by default.">
                <input
                  className={inputCls}
                  value={form.translate.llm_model || ''}
                  onChange={(e) => set('translate', 'llm_model', e.target.value)}
                  placeholder={tcfg?.cheap_defaults?.[form.translate.llm_provider || tcfg?.provider] || tcfg?.model || 'e.g. gemini-2.5-flash'}
                />
              </Field>
            </>
          )}
          <Field label="Target language">
            <input className={inputCls} value={form.translate.target_language} onChange={(e) => set('translate', 'target_language', e.target.value)} placeholder="e.g. English" />
          </Field>
          <Field label="Source language" hint="Blank = auto-detect.">
            <input className={inputCls} value={form.translate.source_language} onChange={(e) => set('translate', 'source_language', e.target.value)} placeholder="auto" />
          </Field>
          <Field label="Domain" hint="Optional terminology domain.">
            <input className={inputCls} value={form.translate.domain} onChange={(e) => set('translate', 'domain', e.target.value)} placeholder="general" />
          </Field>
        </div>
      </div>

      {/* Overviews */}
      {form.overviews && <OverviewDefaults form={form} set={set} tcfg={tcfg} />}
    </Section>
  )
}

// Settings → Overviews (doc/plan-overviews.md): which model writes the plan,
// prompt pack, script and chapters; which STT reads a dropped NotebookLM file;
// what a new series starts as; image backend; publish privacy; podcast show
// metadata. Everything is a per-call default — a series/episode choice wins.
function OverviewDefaults({ form, set, tcfg }) {
  const o = form.overviews
  const s = (k, v) => set('overviews', k, v)
  const llmProvider = o.llm_provider || tcfg?.provider || ''
  const hints = LLM_MODEL_HINTS[llmProvider] || []
  return (
    <div className="pt-2 border-t border-parchment-border dark:border-midnight-600">
      <h3 className="font-display text-sm uppercase tracking-[0.14em] text-gold-deep dark:text-gold mb-3 mt-3">
        Overviews
      </h3>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Writing LLM" hint="Plans, prompt packs, scripts, chapters. Blank = the project's engine model (the one Ask uses).">
          <select className={inputCls} value={o.llm_provider || ''} onChange={(e) => { s('llm_provider', e.target.value); s('llm_model', '') }}>
            <option value="">Engine default{tcfg?.provider ? ` (${tcfg.provider})` : ''}</option>
            {(tcfg?.available_providers ?? []).map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Writing model" hint={hints.length ? `e.g. ${hints.join(', ')}` : 'Blank = the provider\u2019s default model.'}>
          <input className={inputCls} list="overview-llm-models" value={o.llm_model || ''} onChange={(e) => s('llm_model', e.target.value)} placeholder={hints[0] || 'provider default'} />
          <datalist id="overview-llm-models">{hints.map((m) => <option key={m} value={m} />)}</datalist>
        </Field>
        <Field label="Briefs" hint="Episode briefs are Ask answers — they always use the project's retrieval strategy and model.">
          <input className={inputCls} value="Ask (project model)" disabled readOnly />
        </Field>

        <Field label="STT for dropped files" hint="Transcribes the NotebookLM audio/video you drop in. Blank = the Transcription defaults above (an omitted provider means local Whisper on the CPU).">
          <select className={inputCls} value={o.stt_provider || ''} onChange={(e) => s('stt_provider', e.target.value)}>
            {TRANSCRIBE_PROVIDERS.map((p) => <option key={p} value={p}>{p || `Transcription default${form.transcribe?.provider ? ` (${form.transcribe.provider})` : ''}`}</option>)}
          </select>
        </Field>
        <Field label="STT model size" hint="Whisper sizes; Deepgram/AssemblyAI ignore it.">
          <select className={inputCls} value={o.stt_model_size || ''} onChange={(e) => s('stt_model_size', e.target.value)}>
            {WHISPER_SIZES.map((m) => <option key={m} value={m}>{m || 'Transcription default'}</option>)}
          </select>
        </Field>
        <Field label="YouTube privacy" hint="Pre-selected when publishing. Private-first is the safe default.">
          <select className={inputCls} value={o.yt_privacy || 'private'} onChange={(e) => s('yt_privacy', e.target.value)}>
            <option value="private">private</option>
            <option value="unlisted">unlisted</option>
            <option value="public">public</option>
          </select>
        </Field>

        <Field label="New series: language">
          <select className={inputCls} value={o.language || 'cs'} onChange={(e) => s('language', e.target.value)}>
            {OVERVIEW_LANGS.map(([c, n]) => <option key={c} value={c}>{n} ({c})</option>)}
          </select>
        </Field>
        <Field label="New series: format">
          <select className={inputCls} value={o.format || 'audio'} onChange={(e) => s('format', e.target.value)}>
            <option value="audio">Audio</option>
            <option value="video">Video</option>
          </select>
        </Field>
        <Field label="New series: channel">
          <select className={inputCls} value={o.channel || 'youtube'} onChange={(e) => s('channel', e.target.value)}>
            <option value="youtube">YouTube</option>
            <option value="podcast">Podcast (RSS feed)</option>
            <option value="manual">Manual</option>
          </select>
        </Field>
        <Field label="New series: generator" hint="handoff = NotebookLM makes the media, native = Veralis TTS + slides.">
          <select className={inputCls} value={o.generator || 'handoff'} onChange={(e) => s('generator', e.target.value)}>
            <option value="handoff">NotebookLM handoff</option>
            <option value="native">Native (TTS + slides)</option>
          </select>
        </Field>
        <Field label="Native voices: provider" hint="Blank = Google (Chirp 3 HD).">
          <select className={inputCls} value={o.voice_provider || ''} onChange={(e) => s('voice_provider', e.target.value)}>
            <option value="">Google (default)</option>
            <option value="google">google</option>
            <option value="elevenlabs">elevenlabs</option>
          </select>
        </Field>
        <Field label="Native voices: host A / host B" hint="Voice names (Google) or voice IDs (ElevenLabs), comma-free. Blank = engine defaults. Default: Tatínek Pro (A) + Jana (B) from the ElevenLabs account.">
          <div className="flex gap-2">
            <input className={inputCls} value={o.voice_a || ''} onChange={(e) => s('voice_a', e.target.value)} placeholder="A" />
            <input className={inputCls} value={o.voice_b || ''} onChange={(e) => s('voice_b', e.target.value)} placeholder="B" />
          </div>
        </Field>
        <Field label="Native voices: model" hint="ElevenLabs model for new series: eleven_v4 (default, newest), eleven_v3 (expressive, audio tags; speed setting ignored), eleven_multilingual_v2 (stable). Ignored by Google. Each series can override it.">
          <input className={inputCls} value={o.voice_model || ''} onChange={(e) => s('voice_model', e.target.value)} placeholder="eleven_v4" disabled={(o.voice_provider || '') !== 'elevenlabs'} />
        </Field>
        <Field label="Native voices: who speaks (A / B)" hint="The script writer needs the grammatical gender of each voice (Czech: průvodce vs průvodkyně, řekl vs řekla). Default: A male (Tatínek Pro), B female (Jana).">
          <div className="flex gap-2">
            <select className={inputCls} value={o.voice_a_gender || 'male'} onChange={(e) => s('voice_a_gender', e.target.value)}>
              <option value="male">A · man</option>
              <option value="female">A · woman</option>
            </select>
            <select className={inputCls} value={o.voice_b_gender || 'female'} onChange={(e) => s('voice_b_gender', e.target.value)}>
              <option value="male">B · man</option>
              <option value="female">B · woman</option>
            </select>
          </div>
        </Field>
        <Field label="Delivery marks (prosody)" hint="ElevenLabs v3/v4 perform inline audio tags: breaths [inhalation], [pause], [sighs], a written “Hmm,” … Light ≈ one mark per 4–5 turns; rich ≈ one per 2–3 turns incl. [laughs] / [clears throat]. Ignored by Google and multilingual_v2 (marks are stripped before TTS). Captions and transcript never show them.">
          <select className={inputCls} value={o.prosody || 'light'} onChange={(e) => s('prosody', e.target.value)}>
            <option value="off">Off</option>
            <option value="light">Light (default)</option>
            <option value="rich">Rich</option>
          </select>
        </Field>
        <Field label="Music bed (native episodes)" hint="Instrumental under the dialogue, fitted to the episode length (looped or trimmed), faded in and out, turned down while the hosts speak. elevenlabs = one 60 s track per prompt from Eleven Music (≈ 820 credits per minute of music, cached); file = your own mp3/wav. Each series can override file, prompt, volume and fades.">
          <select className={inputCls} value={o.music_bed || 'off'} onChange={(e) => s('music_bed', e.target.value)}>
            <option value="off">Off</option>
            <option value="elevenlabs">Eleven Music (generated)</option>
            <option value="file">Own file</option>
          </select>
        </Field>
        {(o.music_bed || 'off') !== 'off' && (
          <>
            {o.music_bed === 'file' ? (
              <Field label="Music file" hint="Absolute path to an mp3/wav on this machine; it is looped to the episode length.">
                <input className={inputCls} value={o.music_file || ''} onChange={(e) => s('music_file', e.target.value)} placeholder="C:/music/bed.mp3" />
              </Field>
            ) : (
              <Field label="Music prompt" hint="English, describe mood and instruments; the track is forced instrumental. Blank = calm felt piano + pads, no drums.">
                <textarea className={`${inputCls} h-16`} value={o.music_prompt || ''} onChange={(e) => s('music_prompt', e.target.value)} placeholder="Calm, warm ambient instrumental: soft felt piano, slow pads, no drums, no vocals, loopable" />
              </Field>
            )}
            <Field label="Music volume: in pauses % / under speech %" hint="Volume of the bed where nobody speaks, in percent of full scale (12 % ≈ −18 dB), and what percentage of that remains while a host speaks (25 % ≈ −12 dB).">
              <div className="flex gap-2">
                <input className={inputCls} type="number" step="1" min="1" max="100" value={o.music_volume_pct ?? 12} onChange={(e) => s('music_volume_pct', e.target.value)} />
                <input className={inputCls} type="number" step="1" min="1" max="100" value={o.music_duck_pct ?? 25} onChange={(e) => s('music_duck_pct', e.target.value)} />
              </div>
            </Field>
            <Field label="Music fade in / fade out (s)" hint="The bed fades in at the start and out at the end; it is looped or trimmed to the episode length.">
              <div className="flex gap-2">
                <input className={inputCls} type="number" step="0.5" min="0" max="30" value={o.music_fade_in_s ?? 2} onChange={(e) => s('music_fade_in_s', e.target.value)} />
                <input className={inputCls} type="number" step="0.5" min="0" max="30" value={o.music_fade_out_s ?? 4} onChange={(e) => s('music_fade_out_s', e.target.value)} />
              </div>
            </Field>
          </>
        )}

        <Field label="Thumbnail image backend" hint="Blank = auto (Gemini when GOOGLE_API_KEY is set, else Vertex). Vertex needs aiplatform enabled on the project.">
          <select className={inputCls} value={o.image_provider || ''} onChange={(e) => { s('image_provider', e.target.value); s('image_model', '') }}>
            <option value="">Auto</option>
            <option value="gemini">Gemini API (Nano Banana)</option>
            <option value="vertex">Vertex AI Imagen</option>
          </select>
        </Field>
        <Field label="Thumbnail image model" hint="Blank = gemini-nano-banana-2.1 / imagen-4.0-fast-generate-001. Gemini also serves gemini-3.1-flash-image (Nano Banana 2) and gemini-2.5-flash-image.">
          <input className={inputCls} value={o.image_model || ''} onChange={(e) => s('image_model', e.target.value)} disabled={!o.image_provider} placeholder={o.image_provider === 'vertex' ? 'imagen-4.0-fast-generate-001' : 'gemini-nano-banana-2.1'} />
        </Field>
        <Field label="Video outro card" hint="A closing card listing the family (deep dives, next part) on every video: a silent tail on native videos, composed with a spoken sentence around NotebookLM drops.">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={o.outro_enabled !== false && o.outro_enabled !== 'false'} onChange={(e) => s('outro_enabled', e.target.checked)} /> enabled</label>
        </Field>
        <Field label="Video intro card (seconds)" hint="A card with the episode title and description holds this long, then fades into the video — native slides and NotebookLM drops alike. 0 = no card.">
          <input className={inputCls} type="number" min="0" max="15" step="0.5" value={o.video_intro_s ?? 4} onChange={(e) => s('video_intro_s', e.target.value)} />
        </Field>
        <Field label="Slide renderer" hint="Native slides video. HTML = Veralis templates rendered in the installed Chrome/Edge (bullets appear as the hosts reach them, speaker chip, progress bar, the AI thumbnail as image panel, 1080p). ffmpeg = plain stills. Auto picks HTML when a browser is found.">
          <select className={inputCls} value={o.slide_renderer || 'auto'} onChange={(e) => s('slide_renderer', e.target.value)}>
            <option value="auto">auto</option>
            <option value="html">HTML templates (Chrome)</option>
            <option value="ffmpeg">ffmpeg stills</option>
          </select>
        </Field>
        <Field label="Term cards" hint="HTML slides only: a lower third with the explanation when a host first says a key term of the brief. The text comes from the brief itself (no LLM); bold phrases without an explanation there get no card.">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={o.term_cards !== false && o.term_cards !== 'false'} onChange={(e) => s('term_cards', e.target.checked)} /> enabled</label>
        </Field>
        <Field label="Prices for episode costs (USD)" hint="API list prices the cost lines of episodes and series are booked at: ElevenLabs v2/v3/v4 per 1 000 characters (Flash/Turbo count half), Google TTS per 1 000 characters, one AI thumbnail, one minute of Eleven Music, Deep Research per million input / output tokens of a run (the input price is effective: Google bills about a quarter of it as Gemini 3 Pro and the rest as Gemini 3 Flash; calibrated on the November 2026 bill). A change applies to what is booked from then on.">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {[['price_tts_elevenlabs_1k', 'ElevenLabs / 1k chars', 0.08], ['price_tts_google_1k', 'Google TTS / 1k chars', 0.03], ['price_image', 'AI thumbnail', 0.05],
              ['price_music_min', 'Eleven Music / min', 0.15], ['price_research_in_1m', 'Research / 1M in (effective)', 0.82], ['price_research_out_1m', 'Research / 1M out', 12]].map(([k, label, d]) => (
              <label key={k} className="block space-y-1">
                <span className="text-[11px] text-ink-soft dark:text-mist/50">{label}</span>
                <input className={inputCls} type="number" step="0.01" min="0" value={o[k] ?? d} onChange={(e) => s(k, e.target.value === '' ? d : Number(e.target.value))} />
              </label>
            ))}
          </div>
        </Field>
        <Field label="Deep research depth" hint="Gemini Deep Research on the gaps of an episode, started by hand in its Brief tab. Standard ≈ 1–3 USD a run; Max is more thorough, ≈ 3–7 USD. Uses the Gemini key from bff/.env.">
          <select className={inputCls} value={o.research_agent || 'standard'} onChange={(e) => s('research_agent', e.target.value)}>
            <option value="standard">standard</option>
            <option value="max">max</option>
          </select>
        </Field>
        <Field label="Deep dives: YouTube playlist" hint="Where a series' deep dives go by default (each series can override): the series playlist, or a separate “{series} · Deep dives” playlist.">
          <select className={inputCls} value={o.deepdive_playlist || 'same'} onChange={(e) => s('deepdive_playlist', e.target.value)}>
            <option value="same">same playlist as the series</option>
            <option value="separate">separate “· Deep dives” playlist</option>
          </select>
        </Field>
        <Field label="Deep dives: default length" hint="Audio: short ≈ 8–12 · default ≈ 12–20 · long ≈ 20–35 min (NotebookLM Shorter / Default / Longer; native ×1.5 / ×2 / ×3 words). Video: NotebookLM renders ≈ 9 min whatever it gets — here the length only sets the sub-topic count (2–3 / 3–4 / 4–5). Chosen per deep dive when planning.">
          <select className={inputCls} value={o.deepdive_length || 'default'} onChange={(e) => s('deepdive_length', e.target.value)}>
            <option value="short">short</option>
            <option value="default">default</option>
            <option value="long">long</option>
          </select>
        </Field>
        <Field label="Deep dives: retrieval depth (k)" hint="Chunks retrieved per ask of a deep brief (overview briefs use 15).">
          <input className={inputCls} type="number" min="5" max="50" step="1" value={o.deepdive_k ?? 25} onChange={(e) => s('deepdive_k', e.target.value)} />
        </Field>

        <Field label="Podcast: author" hint="Show metadata for NEW podcast series (editable per series afterwards).">
          <input className={inputCls} value={o.podcast_author || ''} onChange={(e) => s('podcast_author', e.target.value)} placeholder="Veralis" />
        </Field>
        <Field label="Podcast: owner e-mail" hint="Apple/Spotify verify the feed through it. Blank = not published in the feed.">
          <input className={inputCls} value={o.podcast_email || ''} onChange={(e) => s('podcast_email', e.target.value)} placeholder="you@example.com" />
        </Field>
        <Field label="Podcast: category" hint="Apple category name, e.g. Education, Technology, Science.">
          <input className={inputCls} value={o.podcast_category || ''} onChange={(e) => s('podcast_category', e.target.value)} placeholder="Education" />
        </Field>
      </div>
      <TemplateGallery />
    </div>
  )
}

// ============================ local-rag (read-only) ============================
function RagEngineCard({ state }) {
  if (!state?.ok) {
    return (
      <Section title="local-rag" subtitle="Retrieval engine">
        <p className="text-sm text-red-600">Unavailable: {state?.error || 'not connected'}</p>
      </Section>
    )
  }
  const c = state.config
  return (
    <Section
      title="local-rag"
      subtitle="Retrieval engine — configured via its environment (.env)."
    >
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
        <Row label="LLM" value={`${c.llm.provider} · ${c.llm.model || '—'}`} />
        <Row label="Embedding" value={`${c.embedding.provider} · ${c.embedding.model || '—'}`} />
        <Row label="Current project" value={c.current_project || '—'} />
        <Row label="Vector store" value={c.vector_db_base} mono />
      </div>
      <div>
        <span className="block text-xs font-medium text-ink-soft dark:text-mist/60 mb-2">API keys</span>
        <div className="flex flex-wrap gap-2">
          {RAG_KEYS.map((k) => <KeyChip key={k} name={k} present={c.api_keys_present?.[k]} />)}
        </div>
      </div>
      <p className="flex items-center gap-1.5 text-[11px] text-ink-soft/70 dark:text-mist/40">
        <Lock className="w-3 h-3" /> Read-only — edit local-rag&apos;s .env and restart the engine to change these.
      </p>
    </Section>
  )
}

function Row({ label, value, mono }) {
  return (
    <div className="flex flex-col">
      <span className="text-[11px] uppercase tracking-wide text-ink-soft/70 dark:text-mist/40">{label}</span>
      <span className={'text-ink dark:text-mist break-all ' + (mono ? 'font-mono text-xs' : '')}>{value}</span>
    </div>
  )
}

// ============================ transcribe (editable) ============================
function TranscribeEngineCard({ state }) {
  const save = useSaveTranscribeConfig()
  const [provider, setProvider] = useState(null)
  const [model, setModel] = useState(null)
  const [keys, setKeys] = useState({})
  const [saved, setSaved] = useState(false)

  const c = state?.ok ? state.config : null
  useEffect(() => {
    if (c && provider === null) {
      setProvider(c.llm.provider || '')
      setModel(c.llm.model || '')
    }
  }, [c, provider])

  if (!state?.ok) {
    return (
      <Section title="transcribe" subtitle="Transcription engine">
        <p className="text-sm text-red-600">Unavailable: {state?.error || 'not connected'}</p>
      </Section>
    )
  }

  const available = c.llm.available_models || {}
  const modelsForProvider = available[provider] || []

  const onProvider = (p) => {
    setProvider(p)
    const list = available[p] || []
    setModel(list[0] || '')
    setSaved(false)
  }
  const setKey = (k, v) => {
    setKeys((s) => ({ ...s, [k]: v }))
    setSaved(false)
  }
  const onSave = () => {
    const api_keys = {}
    for (const [k, v] of Object.entries(keys)) if (v && v.trim()) api_keys[k] = v.trim()
    const llm = { provider, model }
    if (Object.keys(api_keys).length) llm.api_keys = api_keys
    save.mutate(
      { llm },
      {
        onSuccess: () => {
          setSaved(true)
          setKeys({}) // never keep raw key values around in the form
        },
      }
    )
  }

  return (
    <Section
      title="transcribe"
      subtitle="Transcription engine — analysis/cleanup LLM and provider keys."
      footer={
        <>
          <SaveButton onClick={onSave} saving={save.isPending} saved={saved && !save.isPending} />
          {save.data?.coerced?.length > 0 && (
            <span className="text-xs text-gold-deep dark:text-gold">
              Adjusted: {save.data.coerced.join('; ')}
            </span>
          )}
          {save.isError && <span className="text-sm text-red-600">{save.error?.message}</span>}
        </>
      }
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="LLM provider" hint="Used for transcript cleanup & analysis.">
          <select className={inputCls} value={provider ?? ''} onChange={(e) => onProvider(e.target.value)}>
            {Object.keys(available).map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </Field>
        <Field label="Model">
          <select className={inputCls} value={model ?? ''} onChange={(e) => { setModel(e.target.value); setSaved(false) }}>
            {modelsForProvider.length === 0 && <option value={model || ''}>{model || '—'}</option>}
            {modelsForProvider.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </Field>
      </div>

      <div>
        <span className="block text-xs font-medium text-ink-soft dark:text-mist/60 mb-2">API keys</span>
        <div className="flex flex-wrap gap-2 mb-3">
          {TRANSCRIBE_KEYS.map((k) => <KeyChip key={k} name={k} present={c.api_keys_present?.[k]} />)}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {TRANSCRIBE_KEYS.map((k) => (
            <Field key={k} label={`Set ${k} key`}>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-soft dark:text-mist/40" />
                <input
                  type="password"
                  autoComplete="new-password"
                  className={inputCls + ' pl-9'}
                  value={keys[k] ?? ''}
                  onChange={(e) => setKey(k, e.target.value)}
                  placeholder={c.api_keys_present?.[k] ? '•••••• (set — leave blank to keep)' : 'not set'}
                />
              </div>
            </Field>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-ink-soft/70 dark:text-mist/40">
          Keys are write-only: stored securely by the engine, never sent back to the browser. A blank field keeps the existing key.
        </p>
      </div>
    </Section>
  )
}

// ================================== Page ==================================
// LLM spend from the Q&A history (ask calls) — a floor, not the whole bill
// (graph/cached answers are free; lens/impact runs aren't in the history).
function CostsCard() {
  const q = useQuery({
    queryKey: ['rag', 'costs'],
    queryFn: () => bff.get('/api/rag/costs?days=30').then((r) => r.data),
    staleTime: 60000,
  })
  const d = q.data
  const fmt = (c) => `$${(c ?? 0).toFixed(3)}`
  return (
    <Section
      title="LLM costs — last 30 days"
      subtitle="Every LLM call on the platform: asks (Q&A history) + truth lens, impact analysis, scope asks and llm tasks (cost ledger). Graph/cached answers are free."
    >
      {q.isLoading && <div className="text-sm text-ink-soft dark:text-mist/50">Loading…</div>}
      {q.isError && <div className="text-sm text-red-600">{q.error?.message}</div>}
      {d && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-6 text-sm">
            <div><span className="text-2xl font-semibold text-gold-deep dark:text-gold">{fmt(d.total?.cost)}</span>
              <span className="ml-2 text-ink-soft dark:text-mist/50">{d.total?.calls} calls · {(d.total?.tokens ?? 0).toLocaleString()} tokens</span></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-ink-soft dark:text-mist/50 mb-1.5">Per feature</div>
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(d.by_feature ?? {}).map(([f, v]) => (
                    <tr key={f} className="border-b border-parchment-border/60 dark:border-midnight-600/60">
                      <td className="py-1 pr-2">{f}</td>
                      <td className="py-1 text-right font-mono">{fmt(v.cost)}</td>
                      <td className="py-1 pl-3 text-right text-ink-soft dark:text-mist/50">{v.calls}×</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-ink-soft dark:text-mist/50 mb-1.5">Per project</div>
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(d.by_project ?? {}).slice(0, 8).map(([p, v]) => (
                    <tr key={p} className="border-b border-parchment-border/60 dark:border-midnight-600/60">
                      <td className="py-1 pr-2 truncate max-w-[180px]">{p}</td>
                      <td className="py-1 text-right font-mono">{fmt(v.cost)}</td>
                      <td className="py-1 pl-3 text-right text-ink-soft dark:text-mist/50">{v.calls}×</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-ink-soft dark:text-mist/50 mb-1.5">Per day</div>
              <table className="w-full text-sm">
                <tbody>
                  {(d.by_day ?? []).slice(0, 8).map((r) => (
                    <tr key={r.day} className="border-b border-parchment-border/60 dark:border-midnight-600/60">
                      <td className="py-1 pr-2">{r.day}</td>
                      <td className="py-1 text-right font-mono">{fmt(r.cost)}</td>
                      <td className="py-1 pl-3 text-right text-ink-soft dark:text-mist/50">{r.calls}×</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {Object.keys(d.by_model ?? {}).length > 0 && (
            <div className="text-xs text-ink-soft dark:text-mist/50">
              models: {Object.entries(d.by_model).map(([m, v]) => `${m} ${fmt(v.cost)}`).join(' · ')}
            </div>
          )}
        </div>
      )}
    </Section>
  )
}

function EgressCard() {
  const q = useQuery({
    queryKey: ['rag', 'egress'],
    queryFn: () => bff.get('/api/rag/egress').then((r) => r.data),
    staleTime: 300000,
  })
  const d = q.data
  const leaves = d?.leaves_machine
  const Row = ({ label, node }) => {
    if (!node?.provider) return null
    const out = node.leaves_machine
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="w-24 text-ink-soft dark:text-mist/50">{label}</span>
        <span className="font-mono">{node.provider}{node.model ? ` · ${node.model}` : ''}</span>
        <span className={`ml-auto text-[11px] px-2 py-0.5 rounded-full ${out
          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
          : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'}`}>
          {out ? '☁ leaves machine' : '🔒 local'}
        </span>
      </div>
    )
  }
  return (
    <Section
      title="Data egress — where content goes"
      subtitle="Storage is local (Chroma / Neo4j / SQLite), but processing may leave the machine: the embedder at index time and the LLM at query time. This is global engine config — the same for every project."
    >
      {q.isLoading && <div className="text-sm text-ink-soft dark:text-mist/50">Loading…</div>}
      {q.isError && <div className="text-sm text-red-600">{q.error?.message}</div>}
      {d && !d.error && (
        <div className="space-y-3">
          <div className={`text-sm font-medium ${leaves
            ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
            {leaves ? '☁ ' : '🔒 '}{d.summary}
          </div>
          <div className="space-y-1.5 max-w-lg">
            <Row label="Embeddings" node={d.embeddings} />
            <Row label="LLM" node={d.llm} />
          </div>
        </div>
      )}
      {d?.error && <div className="text-sm text-ink-soft dark:text-mist/50">{d.error}</div>}
    </Section>
  )
}

export default function SettingsPage() {
  const { data: engines, isLoading, isError, error } = useEngineSettings()

  return (
    <div className="space-y-8">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold-deep dark:text-gold mb-2">Configuration</div>
        <h1 className="font-display text-4xl text-ink dark:text-mist">Settings</h1>
        <p className="mt-2 text-ink-soft dark:text-mist/60">
          Defaults Veralis applies to every request, and the live configuration of each engine.
        </p>
      </header>

      <DefaultsForm />

      <AccountsPanel />

      <EgressCard />

      <CostsCard />

      <McpUsagePanel />

      <div>
        <h2 className="font-display text-xl text-ink dark:text-mist mb-4">Engines</h2>
        {isLoading && <div className="text-sm text-ink-soft dark:text-mist/50">Loading engine configuration…</div>}
        {isError && <div className="text-sm text-red-600">{error?.message}</div>}
        {engines && (
          <div className="space-y-6">
            <RagEngineCard state={engines.rag} />
            <TranscribeEngineCard state={engines.transcribe} />
          </div>
        )}
      </div>
    </div>
  )
}
